import axios, { AxiosResponse } from 'axios'
import UserAuthModel from '@/models/user/userAuthModel'
import UserPasswordResetRequestModel from '@/models/user/userPasswordResetRequestModel'
import UserTokenModel from '@/models/user/userTokenModel'
import mime from 'mime-types'
import userModule from '@/store/userModule'
import { getModule } from 'vuex-module-decorators'
import TokenRefreshModel from '@/models/user/userTokenRefreshModel'
import AppConfig from '@/vueappconfig'

export const apiClient = axios.create({
  baseURL:
    process.env.NODE_ENV !== 'production'
      ? 'https://localhost:44365/api'
      : AppConfig.API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache'
  }
})

apiClient.interceptors.request.use((request: any) => {
  const tokenJson = localStorage.getItem('user')

  if (!tokenJson) {
    return request
  }

  const user = new UserTokenModel(JSON.parse(tokenJson))

  if (user.accessToken) {
    request.headers['Authorization'] = `Bearer ${user.accessToken}`
  }
  return request
})

// following outline from https://gist.github.com/bragma/f68391596de71e1bfae066be80c259dc
apiClient.interceptors.response.use(undefined, async (error: any) => {
  const response = error.response
  const module = getModule(userModule)
  if (response) {
    if (response.status === 401 && error.config && !error.config._retry) {
      try {
        const user = localStorage.getItem('user')
        if (user) {
          const userModel = new UserTokenModel(JSON.parse(user))
          const tokenRefreshModel = new TokenRefreshModel({
            token: userModel.accessToken,
            refreshToken: userModel.refreshToken
          })
          await module.refreshToken(tokenRefreshModel)
        }
      } catch (authError) {
        return Promise.reject(error)
      }
      error.config._retry = true
      return await apiClient.request(error.config)
    }
  }

  return Promise.reject(error)
})

export default {
  setBaseURL(): void {
    let isProd = localStorage.getItem('env') === 'prod' ? true : false
    const finalURL = isProd ? AppConfig.API_URL : AppConfig.SANDBOX_URL
    apiClient.defaults.baseURL = finalURL
  },

  setUserToken(token: string): string {
    return (apiClient.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${token}`)
  },

  async postLogin(credentials: UserAuthModel): Promise<UserTokenModel> {
    this.setBaseURL()
    const response = await apiClient.post<UserTokenModel>(
      'auth/token',
      credentials
    )
    return response.data
  },
  async refreshToken(token: TokenRefreshModel): Promise<UserTokenModel> {
    this.setBaseURL()
    const response = await apiClient.post(`auth/refreshToken`, token)
    return new UserTokenModel(response.data)
  },
  async sendPasswordReset(
    credentials: UserPasswordResetRequestModel
  ): Promise<AxiosResponse<any>> {
    this.setBaseURL()
    return apiClient.post('user/sendpasswordreset', credentials)
  },
  async postResetPassword(
    id: string,
    password: string
  ): Promise<AxiosResponse<any>> {
    this.setBaseURL()
    return apiClient.post('user/resetpassword/' + id, { password: password })
  },
  async get<T>(url: string, id: string): Promise<T> {
    this.setBaseURL()
    const reponse = await apiClient.get<T>(url + '/' + id)
    return reponse.data
  },
  async getAll<T>(url: string): Promise<Array<T>> {
    this.setBaseURL()
    const response = await apiClient.get<Array<T>>(url)
    return response.data
  },
  async getAllById<T>(url: string, id: string): Promise<Array<T>> {
    this.setBaseURL()
    const response = await apiClient.get<Array<T>>(url + '/' + id)
    return response.data
  },
  async getQuestions<T>(
    url: string,
    extensionId: string,
    stage: string,
    extensionInvestigationId: string
  ): Promise<Array<T>> {
    this.setBaseURL()
    const response = await apiClient.get<Array<T>>(
      url + '/' + extensionId + '/' + stage + '/' + extensionInvestigationId
    )
    return response.data
  },
  async getQuestionsByExtension<T>(
    url: string,
    extensionId: string,
    stage: string
  ): Promise<Array<T>> {
    this.setBaseURL()
    const response = await apiClient.get<Array<T>>(
      url + '/' + extensionId + '/' + stage
    )
    return response.data
  },
  async post<T>(url: string, model: any): Promise<T> {
    this.setBaseURL()
    const response = await apiClient.post<T>(url, model)
    return response.data
  },
  async put<T>(url: string, id: string, model: any): Promise<T> {
    this.setBaseURL()
    const reponse = await apiClient.put<T>(url + '/' + id, model)
    return reponse.data
  },
  async delete(url: string, id: any): Promise<AxiosResponse<any>> {
    this.setBaseURL()
    return apiClient.delete(url + '/' + id)
  },
  async getFile(id: string) {
    this.setBaseURL()
    const response: any = await apiClient.get(
      `extensionCategoryQuestion/considerationsprint/${id}`,
      {
        responseType: 'blob'
      }
    )

    const file = {
      data: response.data,
      extension: mime.extension(response.type)
    }

    return file
  },
  async putOrder<T>(url: string, id: string): Promise<T> {
    this.setBaseURL()
    const reponse = await apiClient.put<T>(url + '/' + id)
    return reponse.data
  }
}
